import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { flows, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Code, CopyButton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Select, PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../i18n/I18nProvider';

/** Which account the ManyChat drawer is for: a channel to connect, or a setup to show again. */
export interface ManyChatTarget {
  channel: string;
  setup?: flows.ManyChatSetup;
}

/**
 * Facebook and Instagram through ManyChat (23 October 2026).
 *
 * Until Meta approves the platform's own messaging permissions, a business's Page can be reached through ManyChat, a
 * partner Meta has approved. Platform staff set it up for the business (acting for it from the platform console), so
 * this section only appears for them. The business sees its connected account on the card above, marked as
 * coming through ManyChat.
 */
export function ManyChatSection({ onConnect }: { onConnect: (channel: string) => void }) {
  const t = useT();
  const info = useQuery({ queryKey: ['meta-connect-info'], queryFn: () => flows.getMetaConnectInfo() });
  if (!info.data?.data.relay_available) return null;

  return (
    <Card withBorder className="rr-channels__elsewhere" data-testid="manychat-section">
      <Stack gap="xs">
        <Text role="bodySmall" fw={600}>{t('channels.manychat.title')}</Text>
        <Text role="bodySmall">{t('channels.manychat.body')}</Text>
        <Group gap="xs">
          <Button size="xs" variant="default" onClick={() => onConnect('messenger')} data-testid="manychat-connect-messenger">
            {t('channels.manychat.connectMessenger')}
          </Button>
          <Button size="xs" variant="default" onClick={() => onConnect('instagram')} data-testid="manychat-connect-instagram">
            {t('channels.manychat.connectInstagram')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** The drawer: paste the ManyChat API key, then copy what ManyChat's Default Reply needs. */
export function ManyChatDrawer({ target, onClose, onDone }: { target: ManyChatTarget | null; onClose: () => void; onDone: () => void }) {
  const t = useT();
  const [channel, setChannel] = useState('messenger');
  const [apiKey, setApiKey] = useState('');
  const [setup, setSetup] = useState<flows.ManyChatSetup | null>(null);

  useEffect(() => {
    if (!target) return;
    setChannel(target.channel === 'instagram' ? 'instagram' : 'messenger');
    setApiKey('');
    setSetup(target.setup ?? null);
  }, [target]);

  const connect = useMutation({
    mutationFn: () => flows.connectManyChat({ channel, api_key: apiKey.trim() }),
    onSuccess: (res) => {
      setSetup(res.data);
      setApiKey('');
      onDone();
    }
  });

  const failure = connect.error
    ? isApiError(connect.error) && connect.error.code === 'NO_PAGE_ACCESS'
      ? t('channels.manychat.error.key')
      : connect.error instanceof Error ? connect.error.message : t('common.unknownError')
    : null;

  return (
    <Drawer opened={!!target} onClose={onClose} title={t('channels.manychat.title')} position="right" size="lg">
      {setup ? (
        <ManyChatSetupPanel setup={setup} />
      ) : (
        <Stack gap="md">
          <Text role="bodySmall">{t('channels.manychat.intro')}</Text>
          <Select
            label={t('channels.manychat.form.channel')}
            value={channel}
            onChange={(e) => setChannel(e.currentTarget.value)}
            data={[
              { value: 'messenger', label: t('inbox.channel.messenger') },
              { value: 'instagram', label: t('inbox.channel.instagram') }
            ]}
          />
          <PasswordInput
            label={t('channels.manychat.form.key')}
            description={t('channels.manychat.form.keyHint')}
            value={apiKey}
            onChange={(e) => setApiKey(e.currentTarget.value)}
            autoComplete="off"
            data-testid="manychat-key"
          />
          {failure && <Alert color="error" title={t('channels.manychat.error.title')}>{failure}</Alert>}
          <Group>
            <Button onClick={() => connect.mutate()} loading={connect.isPending} disabled={apiKey.trim().length === 0} data-testid="manychat-submit">
              {t('channels.manychat.form.submit')}
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

/** What to paste into ManyChat, each with its own copy button, and the steps in order. */
function ManyChatSetupPanel({ setup }: { setup: flows.ManyChatSetup }) {
  const t = useT();
  const rows: { label: string; value: string; testId: string }[] = [
    { label: t('channels.manychat.setup.url'), value: setup.url, testId: 'manychat-url' },
    { label: t('channels.manychat.setup.headerName'), value: setup.header_name, testId: 'manychat-header-name' },
    { label: t('channels.manychat.setup.headerValue'), value: setup.header_value, testId: 'manychat-header-value' },
    { label: t('channels.manychat.setup.body'), value: setup.body, testId: 'manychat-body' }
  ];
  return (
    <Stack gap="md" data-testid="manychat-setup">
      <Alert color="success" title={t('channels.manychat.setup.title', { name: setup.account.label })}>
        {t('channels.manychat.setup.keep')}
      </Alert>
      <Stack gap={4}>
        <Text role="bodySmall" fw={600}>{t('channels.manychat.setup.stepsTitle')}</Text>
        <ol className="rr-channels__steps">
          <li><Text role="bodySmall">{t('channels.manychat.setup.step1')}</Text></li>
          <li><Text role="bodySmall">{t('channels.manychat.setup.step2')}</Text></li>
          <li><Text role="bodySmall">{t('channels.manychat.setup.step3')}</Text></li>
          <li><Text role="bodySmall">{t('channels.manychat.setup.step4')}</Text></li>
          <li><Text role="bodySmall">{t('channels.manychat.setup.step5')}</Text></li>
        </ol>
        <Text role="caption" c="var(--rr-color-text-muted)" data-testid="manychat-test-note">{t('channels.manychat.setup.testNote')}</Text>
      </Stack>
      {rows.map((r) => (
        <Stack gap={4} key={r.testId}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text role="caption" fw={600}>{r.label}</Text>
            <CopyButton value={r.value}>
              {({ copied, failed, copy }) => (
                <Button size="xs" variant="default" onClick={copy}>
                  {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Code block data-testid={r.testId} className="rr-channels__code">{r.value}</Code>
        </Stack>
      ))}
    </Stack>
  );
}
